import { AUTO_STYLE, keyframes, style } from '@angular/animations';
import { Directive, ElementRef, Input, OnInit } from '@angular/core';

import { EnumNgEventName } from '../../shared/event-animation.data';
import { NgContinuousAnimationService } from '../../shared/services/continuous-animation.service';
import { INgAnimationStates, INgAnimationTiming, INgEffectOptions } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function buzz(translateX: number | undefined, rotate: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	translateX = translateX ? translateX : 3;
	rotate 	   = rotate 	? rotate 	 : 2;

	//1 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility 	: AUTO_STYLE,
				transform 	: 'translateX(0) rotate(0)',
				easing 		: 'ease',
				offset 		: 0
			}),
			style({
				transform 	: `translateX(${translateX}px) rotate(${rotate}deg)`,
				easing 		: 'ease',
				offset 		: 0.5
			}),
			style({
				transform 	: `translateX(-${translateX}px) rotate(-${rotate}deg)`,
				easing 		: 'ease',
				offset 		: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * Buzz on Event
 */
@Directive({
    selector: '[ngBuzzOnEvent]',
    providers: [NgContinuousAnimationService],
    standalone: false
})
export class NgBuzzOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomEffectOptions> = null!;
	@Input() targetElement: HTMLElement = null!;

	constructor(
		private elementRef					: ElementRef,
		private continuousAnimationService 	: NgContinuousAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translateX and rotate values are relative to the animation functionality
		if (this.effectOptions?.translateX && this.effectOptions?.translateX < 0) throw new Error(`BuzzOnEventDirective => ngOnInit => FATAL ERROR: Provided translateX value should be positive number: ${this.effectOptions?.translateX}`);
		if (this.effectOptions?.rotate 	   && this.effectOptions?.rotate 	 < 0) throw new Error(`BuzzOnEventDirective => ngOnInit => FATAL ERROR: Provided rotate value should be positive number: ${this.effectOptions?.rotate}`);

		//1 - setup the animation
		this.continuousAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: buzz(this.effectOptions?.translateX, this.effectOptions?.rotate),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay
			}
		});
	}
}


/**------------------------------------------------------
 * Buzz on Mouse Hover
 */
@Directive({
    selector: '[ngBuzzOnMouseHover]',
    providers: [NgContinuousAnimationService],
    standalone: false
})
export class NgBuzzOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() timingOptions: Partial<INgCustomAnimationTiming> = null!;
	@Input() targetElement: HTMLElement 					= null!;

	constructor(
		private elementRef					: ElementRef,
		private continuousAnimationService 	: NgContinuousAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the translateX and rotate values are relative to the animation functionality
		if (this.timingOptions?.translateX && this.timingOptions?.translateX < 0) throw new Error(`BuzzOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided translateX value should be positive number: ${this.timingOptions?.translateX}`);
		if (this.timingOptions?.rotate 	   && this.timingOptions?.rotate 	 < 0) throw new Error(`BuzzOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided rotate value should be positive number: ${this.timingOptions?.rotate}`);

		//1 - setup the animation
		this.continuousAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: buzz(this.timingOptions?.translateX, this.timingOptions?.rotate),
			event			: EnumNgEventName.MouseHover,
			timing			: this.timingOptions
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomEffectOptions extends INgEffectOptions {
	translateX 	: number;
	rotate 		: number;
}

interface INgCustomAnimationTiming extends INgAnimationTiming {
	translateX 	: number;
	rotate 		: number;
}
